<template>
  <el-dialog
    :title="dialogType === 'create' ? 'Számlatétel hozzáadása' : 'Számlatétel szerkesztése'"
    :append-to-body="true"
    :visible="visible"
    :close-on-click-modal="false"
    width="600px"
    @open="onOpen"
    @close="$emit('close')"
  >
    <el-form
      ref="form"
      v-loading="loading"
      :model="item"
      label-position="top"
      :rules="rules"
    >
      <el-radio-group
        v-model="itemType"
        style="margin-bottom: 20px;"
      >
        <el-radio-button label="product">
          Termék
        </el-radio-button>
        <el-radio-button label="service">
          Szolgáltatás
        </el-radio-button>
        <el-radio-button label="free">
          Szabadszavas
        </el-radio-button>
      </el-radio-group>
      <el-form-item
        v-if="itemType === 'product'"
        label="Termék"
      >
        <el-select
          v-model="item.product_id"
          filterable
          :remote="true"
          :remote-method="value => search('products', `?name=${value}`)"
          :loading="productsLoading"
          placeholder="Gépelj a kereséshez..."
          loading-text="Termékek betöltése..."
          style="width: 100%;"
          @change="onProductChange"
        >
          <el-option
            v-for="p in products"
            :key="p.id"
            :value="p.id"
            :label="p.name"
          />
        </el-select>
      </el-form-item>
      <el-form-item
        v-if="itemType === 'service'"
        label="Szolgáltatás"
      >
        <el-select
          v-model="item.service_id"
          filterable
          :remote="true"
          :remote-method="value => search('services', `?name=${value}`)"
          :loading="servicesLoading"
          placeholder="Gépelj a kereséshez..."
          loading-text="Szolgáltatások betöltése..."
          style="width: 100%;"
          @change="onServiceChange"
        >
          <el-option
            v-for="p in services"
            :key="p.id"
            :value="p.id"
            :label="p.name"
          />
        </el-select>
      </el-form-item>
      <el-form-item
        v-if="itemType === 'free'"
        label="Tétel megnevezés"
      >
        <el-input
          v-model="item.product_name"
        />
      </el-form-item>
      <el-form-item label="Megjegyzés">
        <el-input
          v-model="item.comment"
          type="textarea"
          :rows="3"
        />
      </el-form-item>
      <el-form-item
        v-if="itemType === 'product'"
        label="Beszállító"
      >
        <el-select
          v-model="item.supplier_id"
          v-loading="suppliersLoading"
          filterable
          style="width: 100%;"
          @change="setSupplier"
        >
          <el-option
            v-for="s in suppliers"
            :key="s.id"
            :value="s.id"
            :label="s.name"
          />
        </el-select>
      </el-form-item>
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="Mennyiség">
            <el-input-localized-numeric
              v-model="item.count"
            >
              <template slot="append">
                {{ measureUnits.find(unit => unit.value === item.unit_of_measure).label }}
              </template>
            </el-input-localized-numeric>
          </el-form-item>
        </el-col>
        <el-col
          v-if="itemType !== 'product'"
          :span="12"
        >
          <el-form-item label="Mennyiségi egység">
            <el-select
              v-model="item.unit_of_measure"
              filterable
              style="width: 100%;"
            >
              <el-option
                v-for="unit in measureUnits"
                :key="unit.value"
                :value="unit.value"
                :label="unit.label"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col
          v-if="itemType === 'product'"
          :span="12"
        >
          <el-form-item label="Mennyiségi egység">
            <el-select
              v-model="item.product_pack_type_id"
              v-loading="packLoading"
              filterable
              style="width: 100%;"
              @change="onPackChange"
            >
              <el-option
                v-for="unit in packTypes"
                :key="unit.id"
                :value="unit.id"
                :label="unit.pack_type_name"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-form-item label="EAN kód">
        <el-input
          v-model="item.ean"
          v-loading="eanLoading"
        />
      </el-form-item>
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="Nettó egységár">
            <el-input-localized-numeric
              v-model="item.unit_price"
              v-loading="priceLoading"
              @change="onUnitPriceChange"
            >
              <template slot="append">{{ currency }}</template>
            </el-input-localized-numeric>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item
            prop="vat"
            label="ÁFA"
          >
            <el-select
              v-model="item.vat"
              v-loading="vatLoading"
              style="width: 100%;"
              @change="clearVatTypeValidation"
            >
              <el-option
                v-for="vat in vats"
                :key="vat.id"
                :value="vat.value"
                :label="`${vat.value}%`"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item
            prop="vat_type"
            label="ÁFA típus"
          >
            <el-select
              v-model="item.vat_label"
              v-loading="vatLoading"
              style="width: 100%;"
              clearable
            >
              <el-option
                v-for="vatLabel in vatLabels"
                :key="vatLabel"
                :value="vatLabel"
                :label="vatLabel"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row>
        <el-col :span="12">
          <p><strong>Nettó</strong>: {{ netPrice | currency(currency) }}</p>
          <p><strong>Bruttó</strong>: {{ grossPrice | currency(currency) }}</p>
        </el-col>
      </el-row>
    </el-form>
    <div
      slot="footer"
      class="dialog-footer"
    >
      <el-button
        type="primary"
        @click="store"
      >
        {{ dialogType === 'create' ? 'Hozzáadás' : 'Mentés' }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import measureUnits from '../../measureUnits';

export default {
  props: [
    'visible',
    'currency',
    'defaultItem',
    'customerId'
  ],
  data() {
    const validateVat = (rule, value, callback) => {
      if (typeof this.item.vat == 'undefined' || this.item.vat === '') {
        callback(new Error('Az ÁFA megadása kötelező'));
      } else {
        callback();
      }
    };
    const validateVatType = (rule, value, callback) => {
      if (this.item.vat === 0 && (typeof this.item.vat_label == 'undefined' || this.item.vat_label == '')) {
        callback(new Error('Az ÁFA típus megadása kötelező, ha az ÁFA 0%'));
      } else {
        callback();
      }
    };

    return {
      measureUnits,
      vatLabels: [
        'AAM',
        'TAM',
        'EU',
        'EUK',
        'MAA',
        'F.AFA',
        'K.AFA',
        'ÁKK',
      ],
      itemType: 'product',
      item: {
        unit_of_measure: 'PIECE'
      },
      dialogType: 'create',
      productsLoading: false,
      eanLoading: false,
      suppliersLoading: false,
      servicesLoading: false,
      packLoading: false,
      priceLoading: false,
      vatLoading: false,
      loading: false,
      originalUnitPrice: 0,
      products: [],
      services: [],
      suppliers: [],
      packTypes: [],
      vats: [],
      rules: {
          vat: [
            {
              validator: validateVat,
              trigger: 'change'
            }
          ],
          vat_type: [
            {
              validator: validateVatType,
              trigger: 'change'
            }
          ],
        },
    };
  },
  computed: {
    netPrice() {
      return this.item.unit_price * this.item.count;
    },
    grossPrice() {
      return this.netPrice * (1 + this.item.vat / 100);
    },
  },
  methods: {
    async store() {
      this.clearValidation()
      this.$refs.form.validate(async valid => {
        if (valid) {
          this.item.supplier_name = this.item.supplier_id ? this.suppliers.find(partner => partner.id === this.item.supplier_id).name : null;
          let lineItemName = this.item.product_name;
          if (this.item.product_id && this.itemType === 'product') {
            this.item.service_id = null;
            lineItemName = this.products.find(product => product.id === this.item.product_id).name;
          }
          if (this.item.service_id && this.itemType === 'service') {
            this.item.product_id = null;
            this.item.supplier_id = null;
            this.item.supplier_name = null;
            lineItemName = this.services.find(service => service.id === this.item.service_id).name;
          }
          if (this.itemType === 'free') {
            this.item.product_id = null;
            this.item.service_id = null;
            this.item.supplier_id = null;
            this.item.supplier_name = null;
          }
          this.item.product_name = lineItemName;
          this.item.net_price = this.item.unit_price * this.item.count;
          this.item.gross_price = this.item.net_price * (1 + this.item.vat / 100);
          this.item.vat_value = this.item.gross_price - this.item.net_price;
          this.$emit('change', this.item);
          this.$emit('close');
        }
      });
    },
    onUnitPriceChange(price) {
      this.item.unit_price = parseFloat(price)
    },
    async onPackChange(value) {
      const pack = this.packTypes.find(p => p.id === value);
      if (pack) {
        this.$set(this.item, 'unit_of_measure', pack.count_per_pack === 1 ? 'PIECE' : 'CARTON');
        this.$set(this.item, 'unit_price', this.originalUnitPrice * pack.count_per_pack);
        this.$set(this.item, 'product_pack_type_id', pack.id);
        if (this.item.supplier_id) {
          await this.setEanCodes();
        }
      }
    },
    async getPacks() {
      this.packLoading = true;
      this.packTypes = await axios.get(`api/product-pack-types/${this.item.product_id}`);
      this.$set(this.item, 'product_pack_type_id', this.packTypes.find(packType => packType.count_per_pack === 1).id);
      this.packLoading = false;
    },
    async onProductChange(value) {
      await this.getPacks();
      await this.setProductVat();
      await this.setProductPrice();
      await this.setSuppliers();
      await this.setEanCodes();
    },
    async setProductVat() {
      this.vatLoading = true;
      const vat = await axios.get(`api/vats/product/${this.item.product_id}`);
      this.$set(this.item, 'vat', vat);
      this.vatLoading = false;
    },
    async setSuppliers() {
      this.suppliersLoading = true;
      const suppliers = await axios.get(`api/search/suppliers/${this.item.product_id}`);
      this.suppliers = suppliers;
      if (suppliers && suppliers.length) {
        this.$set(this.item, 'supplier_id', suppliers[0].id);
      }
      this.suppliersLoading = false;
    },
    async setEanCodes() {
      if (this.item.supplier_id) {
        this.eanLoading = true;
        try {
          const ean = await axios.get(`api/products/pack-type/${this.item.product_pack_type_id}/supplier/${this.item.supplier_id}/ean`);
          this.$set(this.item, 'ean', ean);
        } catch (e) {}
        this.eanLoading = false;
      }
    },
    async setProductPrice() {
      this.priceLoading = true;
      const price = await axios.get(`api/customer-price-lists/partner/${this.customerId}/product/${this.item.product_id}`);
      this.originalUnitPrice = price;
      this.$set(this.item, 'unit_price', price);
      this.priceLoading = false;
    },
    async setSupplier(value) {
      this.setEanCodes()
    },
    async onOpen() {
      this.loading = true;
      this.setItem();
      if (this.item.key || this.item.id) {
        this.itemType = this.item.product_id ? 'product' : (this.item.service_id ? 'service' : 'free');
        this.dialogType = 'update';
      } else {
        this.itemType = 'product';
        this.dialogType = 'create';
      }
      if (this.item.product_id) {
        this.products = [{ id: this.item.product_id, name: this.item.product_name }];
        this.getPacks();
      }
      if (this.item.service_id) {
        this.products = [{ id: this.item.service_id, name: this.item.product_name }];
      }
      if (this.item.supplier_id) {
        this.suppliers = [{ id: this.item.supplier_id, name: this.item.supplier_name }];
      }
      if (this.vats.length === 0) {
        this.vats = await axios.get('api/search/vats');
        if (this.item.product_id) {
          const vat = await axios.get(`api/vats/product/${this.item.product_id}`);
          this.$set(this.item, 'vat', vat);
        }
      }
      this.loading = false;
    },
    setItem() {
      this.item = { ...this.defaultItem };
    },
    async search(resource, query) {
      if (query !== '') {
        this[`${resource}Loading`] = true;
        this[resource] = await axios.get(`api/search/${resource}${query}`);
        this[`${resource}Loading`] = false;
      }
    },

    async onServiceChange() {
      const service = await axios.get(`api/services/${this.item.service_id}`);
      this.$set(this.item, 'unit_price', service.price);
      this.item.vat = service.vat.value;
      const unit = this.measureUnits.find(m => m.value === service.quantity_unit);
      if (unit) {
        this.item.unit_of_measure = unit.value;
      }
    },

    clearValidation() {
      this.clearVatValidation();
      this.clearVatTypeValidation();
    },

    clearVatValidation() {
      this.$refs['form'].clearValidate('vat');
    },

    clearVatTypeValidation() {
      this.$refs['form'].clearValidate('vat_type');
    }
  },
};
</script>
